import Container from '@mui/material/Container';
import TextField from "@mui/material/TextField";
import { Localized, useLocalization } from "@fluent/react";
import { Box, Button, ButtonGroup, Dialog, Grid, MenuItem, Paper, Tab, TableContainer, Typography } from "@mui/material";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import EditIcon from '@mui/icons-material/Edit';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { selectUserCompanyId } from '../user/userSlice';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import { If } from '../../app/If';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BlockIcon from '@mui/icons-material/Block';
import KeyIcon from '@mui/icons-material/Key';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { SystemEvent, loadSystemEventsForCompany } from '../systemSettings/systemEvents/systemEventsApi';
import { enUS, plPL } from '@mui/x-data-grid/locales';
import { selectSettings } from '../settings/settingsSlice';
import { useNavigate } from 'react-router-dom';
import { AppUser, createAppUser, loadAppUser } from './administration';
import { ArchiveUserDialog, ArchiveUserDialogProps } from './dialogs/ArchiveUserDialog';
import { SendInvitationDialog, SendInvitationDialogProps } from './dialogs/SendInvitationDialog';
import { UnarchiveUserDialog, UnarchiveUserDialogProps } from './dialogs/UnarchiveUserDialog';
import { DeleteUserDialog, DeleteUserDialogProps } from './dialogs/DeleteUserDialog';
import { BlockUserDialog, BlockUserDialogProps } from './dialogs/BlockUserDialog';
import { UnlockUserDialog, UnlockUserDialogProps } from './dialogs/UnlockUserDialog';
import { ResetPasswordDialog, ResetPasswordDialogProps } from './dialogs/ResetPasswordDialog';
import { SaveCompanyUserChangesDialog, SaveCompanyUserChangesDialogProps } from './dialogs/SaveCompanyUserChangesDialog';
import { DotColorChip } from '../../app/components/DotColorChip';
import { CancelButton } from '../../app/components/CancelButton';
import { SaveButton } from '../../app/components/SaveButton';
import { CompanyRole, loadCompanyRoles } from '../administration/companyRoles/companyRolesApi';

type FormTabs = "details" | "events"
export type CompanyUserMode = "create" | "edit"

type UserDialogState =
    | { type: "none" }
    | { type: "archiveUser", props: ArchiveUserDialogProps }
    | { type: "unarchiveUser", props: UnarchiveUserDialogProps }
    | { type: "sendInvitation", props: SendInvitationDialogProps }
    | { type: "blockUser", props: BlockUserDialogProps }
    | { type: "unlockUser", props: UnlockUserDialogProps }
    | { type: "deleteUser", props: DeleteUserDialogProps }
    | { type: "resetPassword", props: ResetPasswordDialogProps }
    | { type: "saveCompanyUserChanges", props: SaveCompanyUserChangesDialogProps }

export const CreateEditCompanyUser = ({ mode }: { mode: CompanyUserMode }) => {
    const navigate = useNavigate()
    const isCreate = mode === "create"

    const { l10n } = useLocalization();
    const { locale } = useAppSelector(selectSettings);
    const { id: paramId } = useParams(); const id = paramId ?? ''
    const [user, setUser] = useState<AppUser | undefined>(undefined)
    const [userLogin, setUserLogin] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [surname, setSurname] = useState<string>("");
    const [role, setRole] = useState<string>("");
    const [companyRoleId, setCompanyRoleId] = useState<string>("");
    const [companyRoles, setCompanyRoles] = useState<CompanyRole[]>([]);
    const [phone, setPhone] = useState<string>("");
    const [status, setStatus] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const [department, setDepartment] = useState<string>("");
    const [isEditing, setIsEditing] = useState(false);
    const [lockedBy, setLockedBy] = useState<string>("");
    const [lockedAt, setLockedAt] = useState<Date | string>("");
    const [lockReason, setLockReason] = useState<string>("");
    const [createDate, setCreateDate] = useState<Date | string>("");
    const isArchived = status === "ARCHIVED"
    const dispatch = useAppDispatch();

    //errors handling
    const [firstNameError, setFirstNameError] = useState(false);
    const [surnameError, setSurnameError] = useState(false);
    const [roleError, setRoleError] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [systemEvents, setSystemEvents] = useState<SystemEvent[]>([]);

    const [userDialog, setUserDialog] = useState<UserDialogState>({ type: "none" })

    const handleGoBackclick = useCallback(() => navigate(-1), [navigate])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const loggedUserCompanyId = useAppSelector(selectUserCompanyId) as string

    useEffect(() => {
        if (isCreate) return;

        dispatch(loadAppUser(id)).then(unwrapResult)
            .then(result => {
                setUser(result);
                setUserLogin(result.userName);
                setFirstName(result.firstName);
                setSurname(result.surname);
                setRole(result.role);
                setCompanyRoleId(result.companyRoleId ?? "");
                setPhone(result.phone);
                setStatus(result.status);
                setPosition(result.position);
                setDepartment(result.departmentName ?? "");
                setLockedBy(result.lockedBy);
                setLockedAt(result.lockedAt ?? "");
                setLockReason(result.lockReason);
                setCreateDate(result.createDate ?? "");
            })
            .catch((error) => console.error(error));

        dispatch(loadSystemEventsForCompany({ companyId: loggedUserCompanyId, query: `?objectType=User&objectId=${id}` }))
            .then(unwrapResult)
            .then(setSystemEvents)
            .catch((error) => console.error(error));
    }, [dispatch, id, isCreate, loggedUserCompanyId]);

    useEffect(() => {
        dispatch(loadCompanyRoles()).then(unwrapResult)
            .then(setCompanyRoles)
            .catch((error) => console.error(error));
    }, [dispatch]);

    // Only plain users have a company role; empty means "keep current / company default".
    const companyRoleIdToSave = role === "user" && companyRoleId ? companyRoleId : null

    const roleValue = role === "user"
        ? (companyRoleId || (companyRoles.find(r => r.isDefault)?.id ?? ""))
        : role
    const roleHint = role === "admin"
        ? <Localized id="user-role-admin-hint">_Pełny dostęp do wszystkich modułów i ustawień firmy</Localized>
        : role === "user"
            ? (companyRoles.find(r => r.id === roleValue)?.description || <Localized id="company-role-picker-hint">_Określa uprawnienia użytkownika</Localized>)
            : undefined

    const [params, setParams] = useSearchParams()
    const tab = isCreate ? 'details' : (params.get('tab') as FormTabs || 'details')
    const setTab = useCallback((tab: FormTabs) => {
        params.set('tab', tab)
        setParams(params, { replace: true })
    }, [params, setParams])

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [setTab])

    const handleCancelEditing = () => {
        if (user) {
            setUserLogin(user.userName);
            setFirstName(user.firstName);
            setSurname(user.surname);
            setRole(user.role);
            setCompanyRoleId(user.companyRoleId ?? "");
            setPhone(user.phone);
            setPosition(user.position);
        }
        setIsEditing(false);
        ClearErrors();
    }

    // Same value the role picker shows: "admin" or the company role (empty = company default).
    const roleLabel = (systemRole: string, roleId: string | null | undefined) =>
        systemRole === "user"
            ? (companyRoles.find(r => r.id === (roleId || companyRoles.find(x => x.isDefault)?.id))?.name ?? "")
            : systemRole === "admin" ? l10n.getString("user-role-admin", null, "Administrator") : systemRole

    const userChanges = user ? [
        { field: l10n.getString("user-login-email", null, "Login / Email"), oldValue: user.userName, newValue: userLogin },
        { field: l10n.getString("user-first-name", null, "Imię"), oldValue: user.firstName, newValue: firstName },
        { field: l10n.getString("user-last-name", null, "Nazwisko"), oldValue: user.surname, newValue: surname },
        { field: l10n.getString("user-role", null, "Rola"), oldValue: roleLabel(user.role, user.companyRoleId), newValue: roleLabel(role, companyRoleId) },
        { field: l10n.getString("user-phone", null, "Telefon"), oldValue: user.phone, newValue: phone },
        { field: l10n.getString("user-position", null, "Stanowisko"), oldValue: user.position, newValue: position },
    ].filter(change => change.oldValue !== change.newValue) : [];

    const handleSaveChangesClick = () => {
        ClearErrors();
        if (!Validate() || !user || !loggedUserCompanyId) return;

        setUserDialog({
            type: "saveCompanyUserChanges",
            props: {
                changes: userChanges,
                user: {
                    ...user,
                    userName: userLogin,
                    email: userLogin,
                    firstName,
                    surname,
                    role,
                    phone,
                    position,
                    companyRoleId: companyRoleIdToSave,
                },
                onSuccessfullySaved: (updatedUser) => {
                    setUser(updatedUser);
                    setIsEditing(false);
                    setUserDialog({ type: "none" });
                },
                onCancel: () => setUserDialog({ type: "none" }),
            },
        });
    };

    const handleCreateClick = async () => {
        ClearErrors();
        if (!Validate()) return;

        try {
            const result = await dispatch(createAppUser({
                userName: userLogin,
                companyId: loggedUserCompanyId,
                email: userLogin,
                firstName,
                surname,
                role,
                phone,
                position,
                status: "NOT_CONFIRMED",
                companyRoleId: companyRoleIdToSave
            })).unwrap();

            dispatch(showSuccess("saved"));
            navigate(`/company/user/edit/${result.id}`, { replace: true });
        } catch (error: any) {
            dispatch(showError(error?.problem?.title ?? "error"));
        }
    }

    const validateEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userLogin)) {
            setEmailError("Nieprawidłowy adres e-mail");
        }
        else {
            setEmailError("");
        }
    };

    function ClearErrors() {
        setFirstNameError(false);
        setRoleError(false);
        setSurnameError(false);
        setEmailError("");
    }

    function Validate() {
        let valResult = true;
        validateEmail()
        if (role === '') {
            valResult = false;
            setRoleError(true);
        }
        if (firstName === '') {
            valResult = false;
            setFirstNameError(true);
        }
        if (surname === '') {
            valResult = false;
            setSurnameError(true);
        }
        if (emailError !== '') {
            valResult = false;

        }

        return valResult;
    }

    const handleUserLoginChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setUserLogin(e.target.value), [setUserLogin]);

    const handleFirstNameChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setFirstName(e.target.value), [setFirstName]);

    const handleSurnameChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setSurname(e.target.value), [setSurname]);

    // One picker for both layers: "admin" is the system role, any other value is a company role of a plain user.
    const handleRoleChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => {
                if (e.target.value === "admin") {
                    setRole("admin")
                } else {
                    setRole("user")
                    setCompanyRoleId(e.target.value)
                }
            }, [setRole, setCompanyRoleId]);

    const handlePhoneChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setPhone(e.target.value), [setPhone]);

    const handlePositionChange: (e: ChangeEvent<HTMLInputElement>) => void =
        useMemo(() =>
            (e) => setPosition(e.target.value), [setPosition]);

    const handleSendInvitationClick = useCallback(() => {
        setUserDialog({
            type: "sendInvitation",
            props: {
                email: userLogin,
                onSuccessfullySaved: (updated: any) => {
                    setUserDialog({ type: "none" });
                    setStatus(updated.status);
                },
                onCancel: () => setUserDialog({ type: "none" })
            }
        });
    }, [setUserDialog, userLogin])

    const handleDeleteUserClick = useCallback(() => {
        setUserDialog({
            type: "deleteUser",
            props: {
                email: userLogin,
                onSuccessfullySaved: (updated: any) => {
                    setUserDialog({ type: "none" });
                    handleGoBackclick();
                },
                onCancel: () => setUserDialog({ type: "none" })
            }
        });
    }, [handleGoBackclick, setUserDialog, userLogin])

    const handleUnlockClick = useCallback(() => {
        setUserDialog({
            type: "unlockUser",
            props: {
                email: userLogin,
                onSuccessfullySaved: (updated: any) => {
                    setUserDialog({ type: "none" });
                    setStatus(updated.status);
                    setLockedAt(updated.lockedAt);
                    setLockedBy(updated.lockedBy);
                    setLockReason(updated.lockReason);
                },
                onCancel: () => setUserDialog({ type: "none" })
            }
        });
    }, [setUserDialog, userLogin])

    const handleResetClick = useCallback(() => {
        setUserDialog({
            type: "resetPassword",
            props: {
                email: userLogin,
                onSuccessfullySaved: (updated: any) => {
                    setUserDialog({ type: "none" });
                    setStatus(updated.status);
                },
                onCancel: () => setUserDialog({ type: "none" })
            }
        });
    }, [setUserDialog, userLogin])

    const handleBlockUser = useCallback(() => {
        setUserDialog({
            type: "blockUser",
            props: {
                email: userLogin,
                onSuccessfullySaved: (updated: any) => {
                    setUserDialog({ type: "none" });
                    setStatus(updated.status);
                    setLockedAt(updated.lockedAt);
                    setLockedBy(updated.lockedBy);
                    setLockReason(updated.lockReason);
                },
                onCancel: () => setUserDialog({ type: "none" })
            }
        });
    }, [setUserDialog, userLogin])

    const handleArchiveUser = useCallback(() => {
        setUserDialog({
            type: "archiveUser",
            props: {
                email: userLogin,
                onSuccessfullySaved: (updated: any) => {
                    setUserDialog({ type: "none" });
                    setStatus(updated.status);
                    setLockedAt(updated.lockedAt);
                    setLockedBy(updated.lockedBy);
                    setLockReason(updated.lockReason);
                },
                onCancel: () => setUserDialog({ type: "none" })
            }
        });
    }, [setUserDialog, userLogin])

    const handleUnarchiveUser = useCallback(() => {
        setUserDialog({
            type: "unarchiveUser",
            props: {
                email: userLogin,
                onSuccessfullySaved: (updated: any) => {
                    setUserDialog({ type: "none" });
                    setStatus(updated.status);
                    setLockedAt(updated.lockedAt);
                    setLockedBy(updated.lockedBy);
                    setLockReason(updated.lockReason);
                },
                onCancel: () => setUserDialog({ type: "none" })
            }
        });
    }, [setUserDialog, userLogin])

    const DialogContentDispatcher = ({ state, setter }: { state: UserDialogState, setter: Dispatch<SetStateAction<UserDialogState>> }) => {
        switch (state.type) {
            case "none":
                return null

            case "archiveUser":
                return <ArchiveUserDialog
                    email={state.props.email}
                    onSuccessfullySaved={state.props.onSuccessfullySaved}
                    onCancel={state.props.onCancel}
                />

            case "unarchiveUser":
                return <UnarchiveUserDialog
                    email={state.props.email}
                    onSuccessfullySaved={state.props.onSuccessfullySaved}
                    onCancel={state.props.onCancel}
                />

            case "sendInvitation":
                return <SendInvitationDialog
                    email={state.props.email}
                    onSuccessfullySaved={state.props.onSuccessfullySaved}
                    onCancel={state.props.onCancel}
                />

            case "blockUser":
                return <BlockUserDialog
                    email={state.props.email}
                    onSuccessfullySaved={state.props.onSuccessfullySaved}
                    onCancel={state.props.onCancel}
                />

            case "unlockUser":
                return <UnlockUserDialog
                    email={state.props.email}
                    onSuccessfullySaved={state.props.onSuccessfullySaved}
                    onCancel={state.props.onCancel}
                />

            case "deleteUser":
                return <DeleteUserDialog
                    email={state.props.email}
                    onSuccessfullySaved={state.props.onSuccessfullySaved}
                    onCancel={state.props.onCancel}
                />

            case "resetPassword":
                return <ResetPasswordDialog
                    email={state.props.email}
                    onSuccessfullySaved={state.props.onSuccessfullySaved}
                    onCancel={state.props.onCancel}
                />

            case "saveCompanyUserChanges":
                return <SaveCompanyUserChangesDialog
                    changes={state.props.changes}
                    user={state.props.user}
                    onSuccessfullySaved={state.props.onSuccessfullySaved}
                    onCancel={state.props.onCancel}
                />

        }
    }

    const columns: GridColDef<SystemEvent, any, any>[] = [
        {
            field: 'createdBy',
            headerName: l10n.getString("created-by", null, "_Utworzył"),
            width: 250
        },
        {
            field: 'eventType',
            headerName: l10n.getString("system-events-code", null, "_Kod zdarzenia"),
            flex: 1
        },
        {
            field: 'eventTypeTranslation',
            headerName: l10n.getString("system-events-type", null, "Zdarzenie"),
            flex: 1,
            valueGetter: (_, row) => {
                return l10n.getString(`system-events-${row.eventType}`, null, `_${row.eventType}`)
            }
        },
        {
            field: 'eventDate',
            headerName: l10n.getString("system-events-date", null, "Data zdarzenia"),
            flex: 1,
            type: 'dateTime',
            valueGetter: (_, row) => row.eventDate !== null ? new Date(row.eventDate) : null,
            valueFormatter: (value) => value ? (value as Date).toLocaleString() : ''
        },
        {
            field: 'description',
            headerName: l10n.getString("system-events-description", null, "Opis"),
            flex: 1,
        },
        {
            field: 'expired',
            headerName: l10n.getString("status", null, "Status"),
            width: 100,

            valueGetter: (_, row) => {
                if(row.eventType === "SendInvitation" && row.eventDate !== null && (new Date().getTime() - new Date(row.eventDate).getTime()) / (1000 * 60 * 60) >= 24) return l10n.getString("system-events-expired", null, "Wygasło")
            }
        }
    ]

    return (
        <Container maxWidth="xl" sx={{
            paddingTop: 2,
            paddingBottom: 2,
        }}>
            <Paper sx={{ width: "100%", maxWidth: "lg", margin: "0 auto", overflow: "hidden" }}>
                <TabContext value={tab}>
                    <TabList onChange={handleTabChange} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
                        <Tab
                            key={0}
                            value={"details"}
                            label={<Localized id="details">Szczegóły</Localized>} />
                        {!isCreate && <Tab
                            key={1}
                            value={"events"}
                            label={<Localized id="system-events-history">Historia zdarzeń</Localized>} />}
                    </TabList>
                    <TabPanel value="details" sx={{ p: { xs: 2, md: 3 } }}>
                        <Grid container spacing={2} sx={{ padding: 2 }}>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                                        <Typography variant="h5">
                                            <Localized id="user-data">Dane użytkownika</Localized>
                                        </Typography>
                                        {!isCreate && status && <DotColorChip
                                            l10nId={`user-status-${status.toLowerCase().replace("_", "-")}`}
                                            label={status}
                                            color={status === "ACTIVE" ? "success" : status === "LOCKED" || status === "ARCHIVED" ? "error" : status === "INVITED" || status === "NOT_CONFIRMED" ? "warning" : "default"}
                                        />}
                                    </Box>
                                    {!isCreate && !isEditing && !isArchived && <Button
                                        color="primary"
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() => setIsEditing(true)}
                                    >
                                        <Localized id="edit-user-data">Zmień dane</Localized>
                                    </Button>}
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    type={"email"}
                                    disabled={isArchived || (!isCreate && (!isEditing || status === "ACTIVE"))}
                                    inputProps={{ maxLength: 50 }}
                                    value={userLogin}
                                    onChange={handleUserLoginChange}
                                    onBlur={validateEmail}
                                    error={!!emailError}
                                    helperText={emailError}
                                    label={<Localized id="user-login-email">_Login / Email</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    required
                                    fullWidth
                                    disabled={isArchived || (!isCreate && !isEditing)}
                                    inputProps={{ maxLength: 50 }}
                                    value={firstName}
                                    error={firstNameError}
                                    onChange={handleFirstNameChange}
                                    label={<Localized id="user-first-name">Imię</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    required
                                    fullWidth
                                    value={surname}
                                    disabled={isArchived || (!isCreate && !isEditing)}
                                    error={surnameError}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={handleSurnameChange}
                                    label={<Localized id="user-last-name">Nazwisko</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    required
                                    fullWidth
                                    disabled={isArchived || (!isCreate && !isEditing)}
                                    value={roleValue}
                                    error={roleError}
                                    onChange={handleRoleChange}
                                    helperText={roleHint}
                                    label={<Localized id="user-role" vars={{ num : 0 }}>Rola</Localized>} >
                                    <MenuItem value="admin"><Localized id="user-role-admin">_Administrator</Localized></MenuItem>
                                    {companyRoles.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                                    {role !== "" && role !== "admin" && role !== "user" && <MenuItem value={role} disabled>{role}</MenuItem>}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={phone}
                                    disabled={isArchived || (!isCreate && !isEditing)}
                                    inputProps={{ maxLength: 20 }}
                                    onChange={handlePhoneChange}
                                    label={<Localized id="user-phone">Phone</Localized>}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={position}
                                    disabled={isArchived || (!isCreate && !isEditing)}
                                    inputProps={{ maxLength: 200 }}
                                    onChange={handlePositionChange}
                                    label={<Localized id="user-position">Stanowisko</Localized>}>
                                </TextField>
                            </Grid>
                            <If condition={!isCreate}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={department}
                                        disabled
                                        label={<Localized id="user-department">_Dział</Localized>}>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={new Date(createDate).toLocaleString()}
                                        disabled
                                        label={<Localized id="registration-date">_Data rejestracji</Localized>}>
                                    </TextField>
                                </Grid>
                            </If>
                            <If condition={lockReason !== null && lockReason !== ""}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={lockedBy}
                                        aria-readonly
                                        disabled={true}
                                        label={status === "ARCHIVED" ? <Localized id="user-archived-by">_Zarchiwizowany przez</Localized> : <Localized id="user-locked-by">_Zablokowany przez</Localized>}>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={new Date(lockedAt).toLocaleString()}
                                        aria-readonly
                                        disabled={true}
                                        label={status === "ARCHIVED" ? <Localized id="user-archiving-date">_Data archiwizacji</Localized> : <Localized id="user-lock-date">Data blokady</Localized>}>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={lockReason}
                                        multiline
                                        rows={4}
                                        aria-readonly
                                        disabled={true}
                                        label={status === "ARCHIVED" ? <Localized id="user-archiving-reason">_Powód archiwizacji</Localized> : <Localized id="user-lock-reason">Powód blokady</Localized>}>
                                    </TextField>
                                </Grid>
                            </If>
                            <Grid item xs={isEditing ? 6 : 4}>
                                {isEditing ? <CancelButton loading={false} onClick={handleCancelEditing} /> : <ButtonGroup color="secondary" variant="contained">
                                    {isCreate && <SaveButton loading={false} onClick={handleCreateClick}/>}
                                    {!isEditing && <Button component={Link} to={`/company/users`} color="secondary" variant="outlined">
                                        <Localized id="back">Wróć</Localized>
                                    </Button>}
                                </ButtonGroup>}
                            </Grid>
                            <Grid item xs={isEditing ? 6 : 8}>
                                <Box display="flex" justifyContent="flex-end" gap={1}>
                                    {isEditing && <SaveButton loading={false} onClick={handleSaveChangesClick}/>}
                                    <If condition={!isEditing && !isCreate && !isArchived && (status === "NOT_CONFIRMED" || status === "INVITED")}>
                                        <Button onClick={handleSendInvitationClick} color="primary" variant="outlined" endIcon={<SendIcon color='secondary' />}>
                                            <Localized id="invite">Zaproś</Localized>
                                        </Button>
                                        <Button onClick={handleDeleteUserClick} color="primary" variant="outlined" endIcon={<DeleteIcon color='secondary' />}>
                                            <Localized id="delete">Usuń</Localized>
                                        </Button>
                                    </If>
                                    <If condition={!isEditing && !isCreate && !isArchived && status === "LOCKED"}>
                                        <Button onClick={handleUnlockClick} color="primary" variant="outlined" endIcon={<LockOpenIcon color='secondary' />}>
                                            <Localized id="unlock">Odblokuj</Localized>
                                        </Button>
                                    </If>
                                    <If condition={!isEditing && !isCreate && !isArchived && (status === "LOCKED" || status === "ACTIVE")}>
                                        <Button onClick={handleArchiveUser} color="primary" variant="outlined" endIcon={<LockOpenIcon color='secondary' />}>
                                            <Localized id="user-archive">Archiwizuj</Localized>
                                        </Button>
                                        <Button onClick={handleResetClick} color="primary" variant="outlined" endIcon={<KeyIcon color='secondary' />}>
                                            <Localized id="reset-password">Resetuj hasło</Localized>
                                        </Button>
                                    </If>
                                    <If condition={!isEditing && !isCreate && !isArchived && status === "ACTIVE"}>
                                        <Button onClick={handleBlockUser} color="primary" variant="outlined" endIcon={<BlockIcon color='error' />}>
                                            <Localized id="user-block">Zablokuj</Localized>
                                        </Button>
                                    </If>
                                    <If condition={!isEditing && !isCreate && status === "ARCHIVED"}>
                                        <Button onClick={handleUnarchiveUser} color="primary" variant="outlined" endIcon={<LockOpenIcon color='secondary' />}>
                                            <Localized id="user-unarchive">_Przywróć</Localized>
                                        </Button>
                                    </If>
                                </Box>
                            </Grid>
                        </Grid>
                    </TabPanel>
                    <TabPanel value="events">
                        <TableContainer component={Paper}>
                            <DataGrid localeText={getCustomLocaleText()}
                                style={{ minHeight: 100, height: 'calc(100vh - 240px)' }}
                                rows={systemEvents}
                                columns={columns}
                                initialState={{
                                    sorting: { sortModel: [{ field: 'eventDate', sort: 'desc' }] }
                                }}
                            />
                        </TableContainer>
                    </TabPanel>
                </TabContext>
            </Paper>
            <Dialog open={userDialog.type !== "none"} fullWidth maxWidth="sm" keepMounted={false}>
                <DialogContentDispatcher state={userDialog} setter={setUserDialog} />
            </Dialog>
        </Container>
    );
}
